import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { SearchField, TableLoading } from '../components';
import {
  WorkingTeamMemberListFilter,
  WorkingTeamMember,
  WorkingTeamMemberState,
  clearWorkingTeamMember,
  fetchWorkingTeamMemberList,
} from '../../state/workingTeamMember';
import { PageFilter } from '../../state/app';
import { AppState } from '../../state/configureStore';

import { TablePageSize } from '../../constants';
import { uniqBy } from 'lodash';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialog: {
      minHeight: '53vh',
    },
    filterContainer: {
      display: 'flex',
      alignItems: 'center',
    },
  }),
);

interface WorkingTeamMemberDialogSearchProps {
  open: boolean;
  handleClose: (res: any) => void;
}

const initialPaging = { pageNumber: 0, pageSize: TablePageSize[0] };

const WorkingTeamMemberDialogSearch: React.FC<WorkingTeamMemberDialogSearchProps> = (
  props,
) => {
  const { open, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    items: WorkingTeamMemberList,
    totalItems,
    loading,
  } = useSelector<AppState, WorkingTeamMemberState>((state) => state.workingTeamMember);
  const [filters, setFilters] = useState<WorkingTeamMemberListFilter>({});
  const [paging, setPaging] = useState<PageFilter>(initialPaging);
  const [selectedWorkingTeamMember, setSelectedWorkingTeamMember] =
    useState<WorkingTeamMember>({});

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { target } = event;
    setFilters((prevState) => ({
      ...prevState,
      [target.name]: target.value,
    }));
    setPaging({
      ...paging,
      pageNumber: 0,
    });
  };

  const handleChangePageNumber = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPaging({ ...paging, pageNumber: newPage });
  };

  const handleChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: +event.target.value,
      pageNumber: 0,
    });
  };

  const resetForm = () => {
    handleClose(undefined);
    setSelectedWorkingTeamMember({});
    setPaging({ ...initialPaging });
    setFilters({});
  };

  useEffect(() => {
    dispatch(fetchWorkingTeamMemberList(filters, paging));
    return function cleanUp() {
      dispatch(clearWorkingTeamMember());
    };
  }, [dispatch, paging, filters]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth={true}
      disableBackdropClick={true}
    >
      <DialogTitle>Select CSTeam Member</DialogTitle>
      <DialogContent>
        <div className={classes.filterContainer}>
          <SearchField
            label="Team"
            onChange={handleChangeFilter}
            filterKey="smWorkingTeam.Name.startsWith"
            filters={filters}
          />
          <SearchField
            label="Name"
            onChange={handleChangeFilter}
            filterKey="User.FullNameWithUserName.Contains"
            filters={filters}
          />
        </div>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"></TableCell>
              <TableCell>No.</TableCell>
              <TableCell>CS Group</TableCell>
              <TableCell>Team</TableCell>
              <TableCell>Name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableLoading isLoading={loading} colSpan={10}>
              {WorkingTeamMemberList.map((workingTeamMember, index) => {
                return (
                  <TableRow key={workingTeamMember.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        name=""
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                          checked,
                        ) => {
                          if (checked) {
                            setSelectedWorkingTeamMember(workingTeamMember);
                          } else {
                            setSelectedWorkingTeamMember({});
                          }
                        }}
                        checked={selectedWorkingTeamMember.id === workingTeamMember.id}
                      />
                    </TableCell>
                    <TableCell>
                      {index + paging.pageSize * paging.pageNumber + 1}
                    </TableCell>
                    <TableCell>
                      {workingTeamMember?.smWorkingTeam?.smWorkingGroup?.name}
                    </TableCell>
                    <TableCell>{workingTeamMember?.smWorkingTeam?.name}</TableCell>
                    <TableCell>{workingTeamMember?.user?.fullNameWithUserName}</TableCell>
                  </TableRow>
                );
              })}
            </TableLoading>
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[...TablePageSize]}
          component="div"
          count={totalItems}
          rowsPerPage={paging.pageSize}
          page={paging.pageNumber}
          onPageChange={handleChangePageNumber}
          onRowsPerPageChange={handleChangePageSize}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            if (setSelectedWorkingTeamMember) {
              setSelectedWorkingTeamMember({});
              handleClose(selectedWorkingTeamMember);
            }
          }}
          variant="contained"
          color="primary"
        >
          Confirm
        </Button>
        <Button
          onClick={() => {
            resetForm();
          }}
          color="default"
          variant="contained"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkingTeamMemberDialogSearch;
